import React, {useState, useEffect, useMemo}		from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';
import {useReactTable, ColumnDef, getCoreRowModel,
	getFilteredRowModel, getSortedRowModel, getFacetedRowModel,
	getFacetedUniqueValues, getFacetedMinMaxValues,
	RowSelectionState}								from '@tanstack/react-table';

import DevicesHistorical							from '../Devices/DevicesHistorical';
import UsersModalAssignDevice						from './UserAssignDevice';
import UsersDocuments								from './UserDocuments';
import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Tabs											from '../ressources/components/Tabs';
import Textarea										from '../ressources/components/Textarea';
import Icon											from '../ressources/components/Icon';
import Avatar										from '../ressources/components/Avatar';
import {DeviceCard}									from '../ressources/components/Device';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';
import ModalFilter									from '../ressources/components/ModalFilter';
import Badge										from '../ressources/components/Badge';
import Section										from '../ressources/components/Section';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import Modal										from '../ressources/components/Modal';
import ModalTags									from '../ressources/components/ModalTags';
import Select										from '../ressources/components/Select';
import Table										from '../ressources/components/Table';
import Checkbox										from '../ressources/components/Checkbox';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {getDevicesApi}								from '../redux/devices/devices';
import {getCompanyUsersApi, putCompanyUsersApi}		from '../redux/companyUsers/companyUsers';
import {getSupportsApi}								from '../redux/support/support';
import {getOrdersApi}								from '../redux/order/order';
import {deleteCompanyUsersApi}						from '../redux/companyUsers/companyUsers';
import {PermissionList, User, UserNotNull}			from '../redux/user/interface';
import {TabsValues}									from '../ressources/components/Tabs/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {PutUserData}								from '../redux/companyUsers/interface';
import {SelectData, SelectValues}					from '../ressources/components/Select/interface';
import {FilterChecked}								from '../ressources/components/Modal/interface';
import {Tags}										from '../ressources/components/ModalTags/interface';
import {Support}									from '../redux/support/interface';

import {ft_formatUserName}							from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';
import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_selectDataString}						from '../ressources/functions/selectData';
import {ft_convertRight}							from '../ressources/functions/convertRight';
import {ft_setVarText}								from '../ressources/functions/text';
import {ft_checkUsersFilters}						from '../ressources/functions/filters';
import {RIGHT_LIST}									from '../ressources/data/rights';

import img_filter									from '../ressources/images/icon/filter.svg';
import img_search									from '../ressources/images/icon/search.svg';
import img_computer									from '../ressources/images/icon/devices/computer.svg';
import img_faceSmile								from '../ressources/images/icon/face-smile.svg';
import img_dotsVertical								from '../ressources/images/icon/dots-vertical.svg';
import img_userMinus								from '../ressources/images/icon/user-minus.svg';
import img_settings									from '../ressources/images/icon/menu/settings.svg';
import img_user										from '../ressources/images/icon/user.svg';
import img_phone									from '../ressources/images/icon/devices/phone-2.svg';
import img_tablet									from '../ressources/images/icon/devices/tablet-2.svg';
import img_truck									from '../ressources/images/icon/truck.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';
import {regex_phone} from '../ressources/data/regex';

import './Users.css';


function Users() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const usersLoad = useAppSelector(state => state.companyUsers.isLoad);
	const usersPutLoad = useAppSelector(state => state.companyUsers.isLoadPut);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const supports = useAppSelector(state => state.support.data);
	const orders = useAppSelector(state => state.order.data);
	const lang = useAppSelector(state => state.settings.lang);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [search, set_search] = useState<InputValues>('');
	const [view, set_view] = useState('');
	const [modal, set_modal] = useState<boolean | 'delete' | 'filter' | 'tags' | PermissionList>(false);
	const [tagsList, set_tagsList] = useState<Tags[]>([]);
	const [load, set_load] = useState(false);

	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [invitationList, set_invitationList] = useState<RowSelectionState>({});
	const [columnVisibility, setColumnVisibility] = useState({});

	// --- Device Data ---
	const [editData, set_editData] = useState<User | undefined>(undefined);

	const [firstname, set_firstname] = useState<InputValues>('');
	const [lastname, set_lastname] = useState<InputValues>('');
	const [job, set_job] = useState<InputValues>('');
	const [email, set_email] = useState<InputValues>('');
	const [phone, set_phone] = useState<InputValues>('');
	const [contractStart, set_contractStart] = useState<InputValues>('');
	const [contractEnd, set_contractEnd] = useState<InputValues>('');
	const [permission, set_permission] = useState<SelectValues>('Aucun');
	const [description, set_description] = useState<InputValues>('');

	const [menu, set_menu] = useState<TabsValues>('details');
	const [change, set_change] = useState(false);
	const [error, set_error] = useState<{[key: string]: string}>({});
	// --- Device Data End ---

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);
	const MENU_DATA = [
		{value: 'details', render: TEXTE.global.details},
		{value: 'documents', render: TEXTE.global.documents},
		{value: 'ticket', render: TEXTE.global.tickets}
	];
	const ACTIONS_LIST: SelectData[] = [
		{
			value:  'tags',
			render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.global.editTags}</span>
		},
		{
			value:  'delete',
			render: <span className={'text-xs weight-500 error-600'}>{TEXTE.global.delete}</span>
		}
	];

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const columns = useMemo<ColumnDef<User>[]>(() => [
		{
			size:   20,
			id:     'select',
			header: ({table}) => (
				<input
					type={'checkbox'}
					{...{
						checked:  table.getIsAllRowsSelected(),
						onChange: table.getToggleAllRowsSelectedHandler()
					}}
				/>
			),
			cell: (v) => {
				if (v.row.original?._id === user?._id)
					return <div />;
				return (
					<input
						type={'checkbox'}
						{...{
							checked:       v.row.getIsSelected(),
							disabled:      !v.row.getCanSelect(),
							onChange:      v.row.getToggleSelectedHandler()
						}}
					/>
				);
			}
		},
		{
			size: 50,
			id:   'profilePicture',
			cell: v => <Avatar size={30} user={v.row.original} />
		},
		{
			id:         'fullName',
			header:     TEXTE.global.name,
			accessorFn: row => ft_formatUserName(row)
		},
		{
			id:         'job',
			header:     TEXTE.global.job,
			accessorFn: row => row?.job || ''
		},
		{
			id:            'devices',
			header:        TEXTE.global.devices,
			cell:          v => <DevicesBadges userData={v.row.original} />
		},
		{
			id:         'companyRight_select',
			header:     TEXTE.users.permission,
			accessorFn: row => row?.data?.companyRight || '',
			cell:       v => (
				<div style={{width: 110}}>
					<Select
						data={ft_selectDataString(RIGHT_LIST)}
						value={v.row.original?.data?.companyRight}
						readOnly={v.row.original?._id === user?._id}
						onChange={val => sendInvitations({[v.row.index]: true}, val as string)}
					/>
				</div>
			)
		},
		{
			id:         'contractStart',
			header:     TEXTE.global.contractStart,
			accessorFn: row => row?.contractStart || new Date(),
			cell:       v => (v.row.original?.contractStart ? moment(v.row.original?.contractStart).format('DD/MM/YY') : '')
		},
		{
			id:         'contractEnd',
			header:     TEXTE.global.contractEnd,
			accessorFn: row => row?.contractEnd || new Date(),
			cell:       v => (v.row.original?.contractEnd ? moment(v.row.original?.contractEnd).format('DD/MM/YY') : '')
		},
		{
			id:         'tags',
			header:     TEXTE.global.tags,
			accessorFn: row => row?.tags.join(' '),
			cell:       v => (
				<div className={'globalDataContainerItemTags'} style={{gap: 4}}>
					{v.row.original?.tags.map((tag, i) => {
						if (i > 1)
							return;
						if (i === 1 && (v.row.original?.tags.length || 0) > 2)
							return <Badge key={i} color={'tags'} text={`+ ${(v.row.original?.tags.length || 0) - 1}`} />;
						return <Badge key={i} color={'tags'} text={tag} />;
					})}
				</div>
			)
		}
	], []);

	const useData = useMemo(() =>
		users.filter(userData => ft_checkUsersFilters(filters, userData))
	, [users, filters]);

	const table = useReactTable({
		columns,
		data:  useData,
		state: {
			columnVisibility,
			globalFilter: search,
			rowSelection: invitationList
		},
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange:     set_invitationList,
		getCoreRowModel:          getCoreRowModel(),
		getFilteredRowModel:      getFilteredRowModel(),
		getSortedRowModel:        getSortedRowModel(),
		getFacetedRowModel:       getFacetedRowModel(),
		getFacetedUniqueValues:   getFacetedUniqueValues(),
		getFacetedMinMaxValues:   getFacetedMinMaxValues(),
		enableRowSelection:       row => row.original?._id !== user?._id
	});

	useEffect(() => {
		dispatch(getDevicesApi(user));
		dispatch(getCompanyUsersApi(user));
	}, []);

	useEffect(() => {
		if (menu !== 'ticket')
			return;

		dispatch(getSupportsApi(user));
		dispatch(getOrdersApi(user));
	}, [menu]);

	useEffect(() => {
		if (params.idUser === 'confirm')
			return set_view(params.idUser);
		const findUser = users.find(find => find?._id === params.idUser);

		setUserData(findUser || null);
		set_view(findUser ? 'edit' : '');
	}, [params.idUser, users]);

	useEffect(() => {
		if (editData)
			set_change(true);
	}, [firstname, lastname, job, email, phone, contractStart, contractEnd, description, permission]
	);

	useEffect(() => set_change(false), [editData]);

	useEffect(() => {
		if (modal)
			return;

		API.getUsersTags(user)
		.then(response => set_tagsList(response?.data || []));
	}, [modal]);

	function editUser() {
		if (!editData)
			return;

		const newError: {[key: string]: string} = {};

		if (phone && !regex_phone.test(phone.toString()))
			newError.phone = TEXTE.users.phoneNotValid;

		set_error(newError);

		if (Object.keys(newError).length > 0)
			return;

		if (!modal && permission !== editData.data?.companyRight && permission !== 'Aucun') {
			if (permission === 'Admin' || !editData.data?.passwordInit)
				return set_modal(permission as PermissionList);
		}

		set_modal(false);

		const apiData: PutUserData = {
			job,
			phone,
			lastname,
			firstname,
			description,
			email:         user?.data?.allowEditEmail ? email : '',
			contractEnd:   contractEnd ? moment(contractEnd, 'DD/MM/YYYY').toDate() : undefined,
			contractStart: contractStart ? moment(contractStart, 'DD/MM/YYYY').toDate() : undefined
		};

		if (permission !== editData.data?.companyRight) {
			apiData.right = ft_convertRight(permission as string);

			if (permission as string !== 'Aucun' && !editData.data?.passwordInit)
				API.initPassword([editData._id], user);
		}

		dispatch(putCompanyUsersApi({idUser: editData._id, data: apiData, user}))
		.then((response) => {
			if (!response.payload?._id) {
				const notificationData = ft_genNotification(TEXTE, 'error', img_faceSmile, response?.payload?.error, 'error');
				return ft_pushNotification(dispatch, notificationId, notificationData);
			}

			const notificationData = ft_genNotification(TEXTE, 'editSuccess', img_faceSmile, ft_formatUserName(response.payload));
			ft_pushNotification(dispatch, notificationId, notificationData);
		});
	}

	function editTags(newTags: string[]) {
		if (load || !editData)
			return;
		set_load(true);

		const apiData: PutUserData = {tags: newTags};

		dispatch(putCompanyUsersApi({idUser: editData._id, data: apiData, user}))
		.then(() => {
			set_load(false);

			const notification = ft_genNotification(TEXTE, 'editTagsSuccess', img_filter);

			ft_pushNotification(dispatch, notificationId, notification);
			set_modal(false);
			set_editData({...editData, tags: newTags});
		});
	}

	function deleteUser() {
		if (!editData)
			return;

		dispatch(deleteCompanyUsersApi({idUser: editData._id, user}))
		.then(() => {
			const notification = ft_genNotification(TEXTE, 'deleteUserSuccess', img_userMinus);
			ft_pushNotification(dispatch, notificationId, notification);

			setUserData(null);
			set_view('');
			set_modal(false);
		});
	}

	function setUserData(userData: User) {
		set_firstname(userData?.firstname || '');
		set_lastname(userData?.lastname || '');
		set_job(userData?.job || '');
		set_email(userData?.email || '');
		set_phone(userData?.phone || '');
		set_contractStart(userData?.contractStart ? moment(userData.contractStart).format('DD/MM/YYYY') : '');
		set_contractEnd(userData?.contractEnd ? moment(userData.contractEnd).format('DD/MM/YYYY') : '');
		set_description(userData?.description || '');
		set_permission(userData?.data?.companyRight);

		set_editData(userData);
		set_view('edit');
	}

	async function sendInvitations(useList: RowSelectionState, permissionName: PermissionList) {
		set_load(true);

		const filteredArray: UserNotNull[] = Object.keys(useList)
		.map(Number)
		.filter(index => index < users.length)
		.map(index => users[index] as UserNotNull);

		const apiData: PutUserData = {
			right: ft_convertRight(permissionName as string)
		};
		let successCount = 0;

		if (permissionName !== 'Aucun') {
			const needInit = filteredArray.filter(e => !e?.data?.passwordInit).map(e => e?._id);

			await API.initPassword(needInit, user);
		}

		await Promise.all(filteredArray.map(userData =>
			new Promise((resolve) => {
				dispatch(putCompanyUsersApi({idUser: userData?._id, data: apiData, user}))
				.then(async (response) => {
					if (!response.payload._id) {
						const notificationData = ft_genNotification(TEXTE, 'error', img_user, response.payload.error, 'error');
						notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
					} else
						successCount++;

					resolve(true);
				});
			})
		));

		const notificationData = ft_genNotification(TEXTE, successCount === 1 ? 'mdmInvitationSendSingle' : 'mdmInvitationSend', img_user, `${successCount}`);
		ft_pushNotification(dispatch, notificationId, notificationData);

		set_load(false);
		set_invitationList({});
	}

	function getSupportName(support: Support, link?: boolean): React.ReactElement {
		let name = <span>{`${support.number}`}</span>;

		if (support.order) {
			const order = orders.find(find => find._id === support.order);

			if (order)
				name = <span onClick={() => link && navigate(`/order/${support.order}`)}>{`${TEXTE.menu.order} n°${order.number}`}</span>;
		} else if (support.device) {
			const device = devicesWithLines.find(find => find._id === support.device);

			if (device)
				name = <span onClick={() => link && navigate(`/devices/${support.device}`)}>{device.shopData?.name || device.name}</span>;
		}

		return link ? <u style={{cursor: 'pointer'}}>{name}</u> : name;
	}

	function renderUsersList() {
		return (
			<div className={'globalDataContainerItem flex column'} style={{overflow: 'auto'}}>
				<div className={'globalListHeader'}>
					<Button
						size={'sm'}
						type={'tertiary-gray'}
						iconBefore={img_filter}
						onClick={() => set_modal('filter')}
					>
						{TEXTE.global.filter}
					</Button>

					<Input
						fullWidth
						value={search}
						onChange={set_search}
						placeholder={TEXTE.users.search}
						before={<img src={img_search} alt={TEXTE.users.search} />}
					/>
				</div>

				<div className={'flex-row'} style={{gap: 8}}>
					<Button
						size={'sm'}
						isLoad={load}
						type={'secondary-gray'}
						disabled={Object.entries(invitationList).length === 0}
						onClick={() => sendInvitations(invitationList, RIGHT_LIST[1])}
					>
						{ft_setVarText(TEXTE.settings.setPermission, RIGHT_LIST[1])}
					</Button>
					<Button
						size={'sm'}
						isLoad={load}
						type={'secondary-gray'}
						disabled={Object.entries(invitationList).length === 0}
						onClick={() => sendInvitations(invitationList, RIGHT_LIST[2])}
					>
						{ft_setVarText(TEXTE.settings.setPermission, RIGHT_LIST[2])}
					</Button>

					<div className={'flex'} />

					<Button
						size={'sm'}
						type={'secondary-gray'}
						onClick={() => set_modal('column')}
					>
						{TEXTE.global.chooseColumn}
					</Button>
				</div>

				<Table
					data={table}
					load={usersLoad && users.length === 0}
					onClickRow={row => navigate(`/users/${row.original?._id}`)}
				/>
			</div>
		);
	}

	function renderDefaultView() {
		return (
			<div className={'devicesSelectMain'}>
				<Icon
					size={40}
					iconBorder
					iconSize={24}
					borderSize={8}
					iconColor={view === 'confirm' ? '#000' : '#6938EF'}
					type={view === 'confirm' ? 'success' : 'primary'}
					icon={view === 'confirm' ? img_faceSmile : img_search}
				/>
				<div className={'devicesSelectTitle'}>
					{TEXTE.users[view === 'confirm' ? 'addUserSuccess' : 'selectUser']}
				</div>
				<div className={'devicesSelectText'}>
					{TEXTE.users[view === 'confirm' ? 'addUserSuccessText' : 'selectUserText']}
				</div>

				<Button
					size={'lg'}
					type={'primary'}
					onClick={() => navigate('/users/onboarding')}
				>
					{TEXTE.users.addUser}
				</Button>
			</div>
		);
	}

	function renderUserAboutSection() {
		let render = <div />;

		if (menu === 'details') {
			render = (
				<div className={'deviceDetailsContainer'}>
					<MultiInputContainer>
						<Input
							value={firstname}
							onChange={set_firstname}
							title={TEXTE.global.firstname}
						/>
						<Input
							value={lastname}
							onChange={set_lastname}
							title={TEXTE.global.lastname}
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						<Input
							value={job}
							onChange={set_job}
							title={TEXTE.global.job}
						/>
						<Select
							value={permission}
							onChange={set_permission}
							title={TEXTE.users.permission}
							data={ft_selectDataString(RIGHT_LIST)}
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						<Input
							readOnly={!user?.data?.allowEditEmail}
							value={email}
							onChange={set_email}
							title={TEXTE.global.email}
						/>
						<Input
							value={phone}
							error={error.phone}
							onChange={set_phone}
							title={TEXTE.global.phone}
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						<Input
							type={'date'}
							value={contractStart}
							onChange={set_contractStart}
							title={TEXTE.global.contractStart}
						/>
						<Input
							type={'date'}
							value={contractEnd}
							onChange={set_contractEnd}
							title={TEXTE.global.contractEnd}
						/>
					</MultiInputContainer>

					<Textarea
						value={description}
						style={{height: 156}}
						onChange={set_description}
						title={TEXTE.global.descriptionAndNote}
					/>
				</div>
			);
		} else if (editData && menu === 'ticket') {
			render = (
				<div className={'globalList'}>
					{supports.filter(s => s.data.creator === editData._id).map((support, i) => {
						let icon = img_truck;

						if (support.device) {
							const device = devicesWithLines.find(find => find._id === support.device);

							if (device?.shopData?.type === 'phone' || device?.notValalaData?.deviceType === 'phone')
								icon = img_phone;
							else if (device?.shopData?.type === 'tablet' || device?.notValalaData?.deviceType === 'tablet')
								icon = img_tablet;
							else
								icon = img_computer;
						}


						return (
							<div
								key={i}
								onClick={() => navigate(`/support/${support._id}`)}
								className={`userCard ${(editData?._id === support._id) && 'selected'}`}
							>
								<div className={'userCardImg'}>
									<Icon
										size={40}
										iconBorder
										icon={icon}
										borderSize={0}
										iconColor={ft_cssVar('primary-600')}
									/>
								</div>
								<div className={'contactListItemInfos'}>
									<span className={`supportStatus ${support.status}`}>{TEXTE.support.status[support.status]}</span>
									<div className={'contractListNumberContainer'}>
										<span className={'weight-500 gray-900'}>{getSupportName(support)}</span>
										{moment(support.lastMessage).isAfter(moment(support.data.lastRead[0]?.date)) && <Badge text={'1'} color={'gray'} />}
									</div>
									<span className={'text-xs gray-600'}>{`${TEXTE.order.the} ${moment(support.data.creationDate).format('DD/MM/YYYY')}`}</span>
								</div>
							</div>
						);
					})}
				</div>
			);
		} else if (menu === 'historical')
			render = <DevicesHistorical />;
		else if (menu === 'documents' && editData)
			render = <UsersDocuments editData={editData} />;

		return (
			<div className={'globalDataAboutContainer'}>
				<Tabs
					data={MENU_DATA}
					actual={menu}
					onChange={set_menu}
				/>

				{render}
			</div>
		);
	}

	function renderButtonContainer() {
		return (
			<ButtonStickyContainer>
				<Button
					fullWidth
					type={'tertiary-gray'}
					onClick={() => set_modal(true)}
				>
					{TEXTE.users.setDevice}
				</Button>

				<Button
					fullWidth
					disabled={!change}
					isLoad={usersPutLoad}
					onClick={() => editUser()}
				>
					{TEXTE.global.saveChange}
				</Button>
			</ButtonStickyContainer>
		);
	}

	function renderUserData() {
		if (!editData)
			return <div className={'globalDataContainer'} />;

		const renderDevices: React.ReactElement[] = [];
		const renderTags: React.ReactElement[] = [];
		let userBadge: React.ReactElement = <></>;

		devicesWithLines.forEach((device, i) => {
			if (device?.user === editData._id)
				renderDevices.push(<DeviceCard link key={i} device={device} />);
		});

		if (renderDevices.length === 0) {
			renderDevices.push(
				<div key={'empty'} className={'usersDataDevicesEmpty'}>
					<Icon
						size={40}
						iconBorder
						iconSize={24}
						borderSize={8}
						icon={img_computer}
						iconColor={'#6938EF'}
					/>
					<span>{TEXTE.users.setDevices}</span>
					<span>{TEXTE.users.emptyDevices}</span>
				</div>
			);
		}

		editData.tags?.forEach(((tag, i) => (
			renderTags.push(<Badge key={`tag-${i}`} text={tag} size={'sm'} color={'tags'} />)
		)));

		if (editData.contractStart && moment(editData.contractStart).isAfter(moment()))
			userBadge = <Badge size={'sm'} color={'gray'} text={TEXTE.users.upComingOnboarding} />;
		else if (editData.contractEnd && moment(editData.contractEnd).isBefore(moment().add('1', 'month')))
			userBadge = <Badge size={'sm'} color={'gray'} text={TEXTE.users.upComingOffboarding} />;

		return (
			<div className={'globalDataContainer'}>

				<Section
					afterTitle={userBadge}
					title={TEXTE.users.yourUser}
				>
					<div className={'globalDataContainerItem'}>
						<Avatar size={40} user={editData} />
						<div className={'usersDataHeaderText'}>
							<span className={'text-md weight-600 gray-900'}>{ft_formatUserName(editData)}</span>
							<span className={'text-sm weight-400 gray-600'}>{editData.job}</span>
						</div>

						<div className={'globalDataContainerItemTags'}>
							{renderTags}
						</div>

						<Button
							absoluteRight
							absoluteHideIcon
							type={'tertiary-gray'}
							absolute={ACTIONS_LIST}
							onChangeAbsolute={(action) => {
								if (action === 'delete' || action === 'tags')
									return set_modal(action);
							}}
						>
							<Icon icon={img_dotsVertical} />
						</Button>
					</div>
				</Section>

				<Section
					title={TEXTE.users.userDevice}
					afterTitle={<DevicesBadges userData={editData} />}
				>
					<div className={'usersDataDevices'}>
						{renderDevices}
					</div>
				</Section>

				<Section title={TEXTE.global.about}>
					{renderUserAboutSection()}
				</Section>

				{renderButtonContainer()}
			</div>
		);
	}

	function renderModal() {
		if (modal === 'delete') {
			return (
				<Modal
					confirmButton={deleteUser}
					onClose={() => set_modal(false)}
					cancelButton={() => set_modal(false)}
					header={
						<Icon
							size={40}
							iconBorder
							iconSize={18}
							borderSize={8}
							type={'warning'}
							icon={img_userMinus}
							iconColor={ft_cssVar('warning-600')}
						/>
					}
				>
					<span className={'text-lg weight-600 gray-900'}>{TEXTE.users.modal.deleteUser}</span>
					<span className={'text-sm gray-600'}>
						{TEXTE.users.modal.deleteUserTextPart1}
						<b>{ft_formatUserName(editData)}</b>
						{TEXTE.users.modal.deleteUserTextPart2}
					</span>
				</Modal>
			);
		}

		if (modal === 'filter') {
			return (
				<ModalFilter
					isUser
					filters={filters}
					tagsList={tagsList}
					setFilters={set_filters}
					onClose={() => set_modal(false)}
				/>
			);
		}

		if (modal === 'tags') {
			return (
				<ModalTags
					load={load}
					allTags={tagsList}
					onConfirm={editTags}
					tags={editData?.tags || []}
					onClose={() => set_modal(false)}
				/>
			);
		}

		if (modal === 'Utilisateur' || modal === 'Admin') {
			return (
				<Modal
					confirmButton={editUser}
					onClose={() => set_modal(false)}
					cancelButton={() => set_modal(false)}
					header={
						<Icon
							size={40}
							iconBorder
							iconSize={18}
							borderSize={8}
							type={'success'}
							icon={img_settings}
							iconColor={[ft_cssVar('success-600'), ft_cssVar('success-600')]}
						/>
					}
				>
					<span className={'text-lg weight-600 gray-900'}>{editData?.data?.passwordInit ? TEXTE.users.modal.adminRightOnly : TEXTE.users.modal.userRight}</span>
					<span className={'text-sm gray-600'}>
						{!editData?.data?.passwordInit && ft_setVarText(TEXTE.users.modal.userRightText, modal)}
						<br />
						{modal === 'Admin' && TEXTE.users.modal.adminRightText}
					</span>
				</Modal>
			);
		}

		if (modal === 'column') {
			const render: React.ReactElement[] = [];

			table.getAllLeafColumns().map((column) => {
				if (!column.columnDef.header || typeof column.columnDef.header !== 'string')
					return;

				render.push(
					<label
						key={column.id}
						className={'modalFilterItem'}
					>
						<Checkbox active={column.getIsVisible()} />
						<span>{column.columnDef.header}</span>
						<input
							type={'checkbox'}
							style={{visibility: 'hidden'}}
							{...{
								checked:  column.getIsVisible(),
								onChange: column.getToggleVisibilityHandler()
							}}
						/>
					</label>
				);
			});

			return (
				<Modal
					modalRight
					onClose={() => set_modal(false)}
				>
					<div className={'modalFilterContainer'}>
						<span>{TEXTE.global.chooseColumn}</span>
						{render}
					</div>
				</Modal>
			);
		}

		return (
			<UsersModalAssignDevice
				modal
				actualUser={editData}
				onClose={() => set_modal(false)}
			/>
		);
	}

	return (
		<div className={'globalDataContainer'}>
			{!view && renderUsersList()}

			{(view === 'confirm') && renderDefaultView()}
			{(view === 'edit') && renderUserData()}

			{modal && renderModal()}
		</div>
	);
}

export default Users;
